import React, { useState } from 'react';
import {
  Card, CardContent, Typography, Button, LinearProgress, Checkbox, FormControlLabel,
  Box, Alert, ButtonGroup, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip
} from '@mui/material';
import { PlayArrow, Refresh, SplitscreenOutlined, Download, InfoOutlined } from '@mui/icons-material';
import { CSVProcessor } from '../../utils/csvProcessor';
import apiClient from '../../utils/apiClient';
import { toast } from 'react-toastify';
import JSZip from 'jszip';

const MigrationPanel = ({ file }) => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle');
  const [results, setResults] = useState({ success: 0, failed: 0 });
  const [totalRecords, setTotalRecords] = useState(0);
  const [batches, setBatches] = useState([]);
  const [showBatches, setShowBatches] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState('');
  const [startRowNo, setStartRowNo] = useState(1);
  const [expandedErrors, setExpandedErrors] = useState({});
  const [fileTranatSamples, setFileTranatSamples] = useState([]);
  const [confirmExistingForAll, setConfirmExistingForAll] = useState(false);

  const migrationTypes = [
    { key: 'groups', label: 'PC Groups', endpoint: 'groups', startRow: 1 },
    { key: 'drvGroups', label: 'DRV Groups', endpoint: 'drvGroups', startRow: 1 },
    { key: 'users', label: 'Users', endpoint: 'users', startRow: 1 },
    { key: 'transactions', label: 'Transactions', endpoint: 'transactions', startRow: 1 }
  ];

  const handleSplitFile = async (endpoint, startRow) => {
    console.log('🚀 Split button clicked for:', endpoint, 'File:', file.name, 'Starting ROWNO:', startRow);
    setSelectedEndpoint(endpoint);
    setStartRowNo(startRow);
    setFileTranatSamples([]);

    if (endpoint === 'transactions') {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      const tranatIdx = headers.findIndex(h => h.toUpperCase() === 'TRANAT');
      if (tranatIdx !== -1) {
        const seen = new Set();
        lines.slice(1).forEach(line => {
          const cols = line.split(',');
          const val = (cols[tranatIdx] || '').trim().replace(/^"|"$/g, '');
          if (val) seen.add(val);
        });
        // Deduplicate by detected pattern (keep one example per pattern)
        const patternMap = {};
        seen.forEach(val => {
          let pattern = 'unknown';
          if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(val)) pattern = 'YYYY-MM-DD HH:MM:SS';
          else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(val)) pattern = 'YYYY-MM-DD HH:MM';
          else if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}$/.test(val)) pattern = 'DD-MM-YYYY HH:MM';
          else if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(val)) pattern = 'DD/MM/YYYY HH:MM';
          else if (/^\d{2}-\d{2}-\d{2} \d{2}:\d{2}$/.test(val)) pattern = 'DD-MM-YY HH:MM';
          else if (/^\d{2}\/\d{2}\/\d{2} \d{2}:\d{2}$/.test(val)) pattern = 'DD/MM/YY HH:MM';
          else if (/^\d{2}-\d{2}-\d{4}$/.test(val)) pattern = 'DD-MM-YYYY';
          else if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) pattern = 'DD/MM/YYYY';
          if (!patternMap[pattern]) patternMap[pattern] = val;
        });
        setFileTranatSamples(Object.entries(patternMap).map(([pattern, example]) => ({ pattern, example })));
      }
    }
    try {
      const fileBatches = await CSVProcessor.splitCSV(file, 100, startRow);
      
      // Read first batch to show sample data
      if (fileBatches.length > 0) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const csvContent = e.target.result;
          const lines = csvContent.split('\n');
          console.log('📊 SAMPLE - First batch headers:', lines[0]);
          console.log('📊 SAMPLE - First 5 rows with ROWNO:');
          lines.slice(1, 6).forEach((line, idx) => {
            console.log(`Row ${idx + 1}:`, line);
          });
        };
        reader.readAsText(fileBatches[0].data);
      }
      
      const batchesWithStatus = fileBatches.map((batch, index) => ({
        ...batch,
        id: index + 1,
        status: 'pending',
        error: null,
        dateErrors: [],
        startRow: startRow + (index * 100),
        endRow: startRow + (index * 100) + batch.rowCount - 1
      }));
      setBatches(batchesWithStatus);
      setTotalRecords(batchesWithStatus.reduce((sum, batch) => sum + batch.rowCount, 0));
      console.log('✅ Split complete! Total batches:', batchesWithStatus.length, 'Total records:', batchesWithStatus.reduce((sum, batch) => sum + batch.rowCount, 0));
      setShowBatches(true);
      setStatus('split');
    } catch (error) {
      console.error('❌ Split failed:', error);
      setStatus('error');
    }
  };

  const handleSingleBatch = async (batchIndex) => {
    const batch = batches[batchIndex];
    if (batch.status === 'processing') return;

    console.log('🚀 Processing batch:', batch.id, 'Endpoint:', selectedEndpoint);
    
    // Read and log the CSV payload
    const reader = new FileReader();
    reader.onload = (e) => {
      const csvContent = e.target.result;
      console.log('📋 PAYLOAD - Batch', batch.id, 'CSV Content:');
      console.log(csvContent);
      const lines = csvContent.split('\n');
      console.log('📊 PAYLOAD - Headers:', lines[0]);
      console.log('📊 PAYLOAD - First 3 rows:', lines.slice(1, 4));
      console.log('📊 PAYLOAD - Total rows in batch:', lines.length - 1);
    };
    reader.readAsText(batch.data);
    
    setBatches(prev => prev.map((b, i) => 
      i === batchIndex ? { ...b, status: 'processing' } : b
    ));

    try {
      let response;
      try {
        response = await apiClient.uploadBatch(
          batch.data,
          apiClient.endpoints[selectedEndpoint],
          0,
          confirmExistingForAll
        );
      } catch (uploadError) {
        const warning = uploadError.response?.status === 409 && uploadError.response?.data?.existing_records;
        if (!warning) throw uploadError;

        const count = uploadError.response.data.existing_records.length;
        const confirmed = confirmExistingForAll || window.confirm(
          `${count} subscription record(s) already exist. Do you want to process them again and update their values?`
        );
        if (!confirmed) {
          toast.info('Batch skipped. Existing subscription records were not changed.');
          setBatches(prev => prev.map((b, i) =>
            i === batchIndex ? { ...b, status: 'failed', error: 'Skipped by user because records already exist.' } : b
          ));
          return;
        }
        if (!confirmExistingForAll) setConfirmExistingForAll(true);
        response = await apiClient.uploadBatch(batch.data, apiClient.endpoints[selectedEndpoint], 0, true);
      }
      console.log('✅ Batch', batch.id, 'success:', response);

      const dateErrors = response.date_errors || [];
      const batchStatus = dateErrors.length > 0 ? 'partial' : 'success';

      setBatches(prev => prev.map((b, i) =>
        i === batchIndex ? { ...b, status: batchStatus, dateErrors } : b
      ));
      setResults(prev => ({ ...prev, success: prev.success + 1 }));

      if (dateErrors.length > 0) {
        toast.warning(`Batch ${batch.id}: ${response.created} created, ${dateErrors.length} date error(s)`);
      } else if (response.message) {
        toast.success(response.message);
      } else {
        toast.success(`Batch ${batch.id} processed successfully`);
      }
    } catch (error) {
      console.error('❌ Batch', batch.id, 'failed:', error);
      let errorMessage = error.message;
      if (error.message.includes('Authentication failed')) {
        errorMessage = 'Authentication failed - please login again';
      }
      setBatches(prev => prev.map((b, i) => 
        i === batchIndex ? { ...b, status: 'failed', error: errorMessage } : b
      ));
      setResults(prev => ({ ...prev, failed: prev.failed + 1 }));
      
      // Show error toast
      toast.error(`Batch ${batch.id} failed: ${errorMessage}`);
    }
  };

  const handleProcessAll = async () => {
    const pendingBatches = batches.filter(b => b.status === 'pending' || b.status === 'failed');
    setStatus('processing');
    
    for (let i = 0; i < pendingBatches.length; i++) {
      const batchIndex = batches.findIndex(b => b.id === pendingBatches[i].id);
      await handleSingleBatch(batchIndex);
      
      const progressPercent = ((i + 1) / pendingBatches.length) * 100;
      setProgress(progressPercent);
    }
    
    setStatus('completed');
  };

  const downloadBatches = async (statusFilter) => {
    const filteredBatches = batches.filter(b => b.status === statusFilter);
    
    if (filteredBatches.length === 0) {
      toast.info(`No ${statusFilter} batches to download`);
      return;
    }

    const zip = new JSZip();
    const folderName = statusFilter === 'success' ? 'successful_batches' : 'failed_batches';
    const folder = zip.folder(folderName);

    for (const batch of filteredBatches) {
      const fileName = `batch_${batch.id}.csv`;
      folder.file(fileName, batch.data);
    }

    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${file.name.replace('.csv', '')}_${folderName}.zip`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success(`Downloaded ${filteredBatches.length} ${statusFilter} batches`);
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {file.name}
        </Typography>
        
        <ButtonGroup variant="outlined" sx={{ mb: 2 }}>
          {migrationTypes.map(type => (
            <Button
              key={type.key}
              onClick={() => handleSplitFile(type.endpoint, type.startRow)}
              disabled={status === 'processing'}
              startIcon={<SplitscreenOutlined />}
            >
              Split for {type.label}
            </Button>
          ))}
        </ButtonGroup>

        <FormControlLabel
          sx={{ display: 'flex', width: 'fit-content', mb: 2, px: 1.5, py: 0.5, borderRadius: 2, backgroundColor: confirmExistingForAll ? 'rgba(22,124,175,.08)' : 'transparent' }}
          control={
            <Checkbox
              checked={confirmExistingForAll}
              onChange={(event) => setConfirmExistingForAll(event.target.checked)}
              disabled={status === 'processing'}
              color="primary"
            />
          }
          label="Approve existing records for all batches"
        />

        {selectedEndpoint === 'transactions' && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <InfoOutlined fontSize="small" color="info" />
              <Typography variant="caption" color="text.secondary">Accepted TRANAT formats:</Typography>
              {['YYYY-MM-DD HH:MM', 'YYYY-MM-DD HH:MM:SS', 'DD-MM-YYYY HH:MM', 'DD/MM/YYYY HH:MM', 'DD-MM-YY HH:MM', 'DD/MM/YY HH:MM', 'MM/DD/YYYY HH:MM', 'DD-MM-YYYY', 'DD/MM/YYYY'].map(fmt => (
                <Chip key={fmt} label={fmt} size="small" variant="outlined" sx={{ fontSize: 10 }} />
              ))}
            </Box>
            {fileTranatSamples.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <InfoOutlined fontSize="small" color="warning" />
                <Typography variant="caption" color="text.secondary">Detected in your file:</Typography>
                {fileTranatSamples.map(({ pattern, example }) => {
                  const accepted = ['YYYY-MM-DD HH:MM', 'YYYY-MM-DD HH:MM:SS', 'DD-MM-YYYY HH:MM', 'DD/MM/YYYY HH:MM', 'DD-MM-YY HH:MM', 'DD/MM/YY HH:MM', 'MM/DD/YYYY HH:MM', 'DD-MM-YYYY', 'DD/MM/YYYY'];
                  const isValid = accepted.includes(pattern);
                  return (
                    <Tooltip key={pattern} title={`Example: ${example}`} arrow>
                      <Chip
                        label={pattern}
                        size="small"
                        color={isValid ? 'success' : 'error'}
                        variant={isValid ? 'filled' : 'filled'}
                        sx={{ fontSize: 10 }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {showBatches && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleProcessAll}
                startIcon={<PlayArrow />}
              >
                Process All Pending
              </Button>
              <Button
                variant="outlined"
                color="success"
                onClick={() => downloadBatches('success')}
                startIcon={<Download />}
                disabled={results.success === 0}
              >
                Download Success ({results.success})
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => downloadBatches('failed')}
                startIcon={<Download />}
                disabled={results.failed === 0}
              >
                Download Failed ({results.failed})
              </Button>
              <Chip label={`Total: ${batches.length} batches`} />
            </Box>
            
            <TableContainer component={Paper} sx={{ maxHeight: 400, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: '#fff', width: 70 }}>Batch #</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: '#fff' }}>Row Range</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: '#fff', width: 80 }}>Records</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: '#fff', width: 100 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: '#fff', width: 120 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: '#fff' }}>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batches.map((batch, index) => (
                    <TableRow key={batch.id} sx={{ bgcolor: index % 2 === 0 ? '#fafafa' : '#fff', '&:hover': { bgcolor: '#e3f2fd' } }}>
                      <TableCell>{batch.id}</TableCell>
                      <TableCell>
                        <Chip label={`${batch.startRow} - ${batch.endRow}`} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{batch.rowCount}</TableCell>
                      <TableCell>
                        <Chip
                          label={batch.status}
                          color={batch.status === 'success' ? 'success' : batch.status === 'partial' ? 'warning' : batch.status === 'failed' ? 'error' : batch.status === 'processing' ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleSingleBatch(index)}
                          disabled={batch.status === 'processing' || batch.status === 'success' || batch.status === 'partial'}
                          startIcon={<PlayArrow />}
                        >
                          {batch.status === 'success' ? 'Done' : batch.status === 'processing' ? 'Processing...' : 'Push'}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 400 }}>
                        {batch.error && (
                          <Typography variant="caption" color="error" sx={{ wordBreak: 'break-word' }}>
                            {batch.error}
                          </Typography>
                        )}
                        {batch.dateErrors?.length > 0 && (
                          <Box>
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                              onClick={() => setExpandedErrors(prev => ({ ...prev, [batch.id]: !prev[batch.id] }))}
                            >
                              <Chip label={`${batch.dateErrors.length} date error(s)`} color="warning" size="small" />
                              <Typography variant="caption" color="warning.main">
                                {expandedErrors[batch.id] ? '▲ Hide' : '▼ Show'}
                              </Typography>
                            </Box>
                            {expandedErrors[batch.id] && (
                              <TableContainer sx={{ mt: 1, border: '1px solid #ffe082', borderRadius: 1, maxHeight: 200 }}>
                                <Table size="small" stickyHeader>
                                  <TableHead>
                                    <TableRow>
                                      {['Row', 'Group', 'Sub', 'Bad Date', 'Error'].map(h => (
                                        <TableCell key={h} sx={{ fontWeight: 'bold', bgcolor: '#f57c00', color: '#fff', py: 0.5, fontSize: 11 }}>{h}</TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {batch.dateErrors.map((e, i) => (
                                      <TableRow key={i} sx={{ bgcolor: '#fffde7' }}>
                                        <TableCell sx={{ fontSize: 11, py: 0.5 }}>{e.row}</TableCell>
                                        <TableCell sx={{ fontSize: 11, py: 0.5 }}>{e.group}</TableCell>
                                        <TableCell sx={{ fontSize: 11, py: 0.5 }}>{e.subscription}</TableCell>
                                        <TableCell sx={{ fontSize: 11, py: 0.5 }}><code>{e.tranat}</code></TableCell>
                                        <TableCell sx={{ fontSize: 11, py: 0.5, color: '#e65100' }}>{e.error}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {status === 'processing' && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Processing... {Math.round(progress)}% ({results.success + results.failed}/{batches.filter(b => b.status !== 'pending').length + batches.filter(b => b.status === 'pending' || b.status === 'failed').length} batches)
            </Typography>
          </Box>
        )}

        {status === 'split' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            File split into {batches.length} batches ({totalRecords} total records). 
            Use "Push" buttons to upload individual batches or "Process All Pending" for bulk upload.
          </Alert>
        )}

        {status === 'completed' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Processing completed! Success: {results.success} | Failed: {results.failed}
          </Alert>
        )}

        {status === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Migration failed. Please try again.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default MigrationPanel;
